// Validates Spanish DNI/NIF (8 digits + letter OR K/L/M + 7 digits + letter with MOD 23 check)
export const validateDNI = (dni: string): boolean => {
    const dniUpper = dni.toUpperCase().trim();
    const letters = 'TRWAGMYFPDXBNJZSQVHLCKE';

    // Standard DNI: 8 digits + letter
    const standardDniRegex = /^[0-9]{8}[A-Z]$/;
    if (standardDniRegex.test(dniUpper)) {
        const number = parseInt(dniUpper.substring(0, 8), 10);
        const letter = dniUpper.charAt(8);
        return letters.charAt(number % 23) === letter;
    }

    // Personal NIF: K/L/M + 7 digits + letter
    const personalNifRegex = /^[KLM][0-9]{7}[A-Z]$/;
    if (personalNifRegex.test(dniUpper)) {
        const firstChar = dniUpper.charAt(0);
        const replacement = firstChar === 'K' ? '0' : firstChar === 'L' ? '1' : '2';
        const number = parseInt(replacement + dniUpper.substring(1, 8), 10);
        const letter = dniUpper.charAt(8);
        return letters.charAt(number % 23) === letter;
    }

    return false;
};

// Validates Spanish NIE (X/Y/Z + 7 digits + letter with MOD 23 check)
export const validateNIE = (nie: string): boolean => {
    const nieUpper = nie.toUpperCase().trim();
    const nieRegex = /^[XYZ][0-9]{7}[A-Z]$/;

    if (!nieRegex.test(nieUpper)) {
        return false;
    }

    // Convert X/Y/Z to 0/1/2 for control letter calculation
    const letters = 'TRWAGMYFPDXBNJZSQVHLCKE';
    const firstChar = nieUpper.charAt(0);
    const replacement = firstChar === 'X' ? '0' : firstChar === 'Y' ? '1' : '2';
    const number = parseInt(replacement + nieUpper.substring(1, 8), 10);
    const letter = nieUpper.charAt(8);

    return letters.charAt(number % 23) === letter;
};

// Validates passport (6-9 alphanumeric characters)
export const validatePassport = (passport: string): boolean => {
    const passportUpper = passport.toUpperCase().trim();
    const passportRegex = /^[A-Z0-9]{6,9}$/;

    return passportRegex.test(passportUpper);
};

// Validates Spanish CIF (Company Tax ID)
// Format: Letter + 7 digits + Control character (digit or letter)
export const validateCIF = (cif: string): boolean => {
    const cifUpper = cif.toUpperCase().trim();
    const cifRegex = /^[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J]$/;

    if (!cifRegex.test(cifUpper)) {
        return false;
    }

    // CIF control character validation
    const digits = cifUpper.substring(1, 8);
    let sum = 0;

    // Sum even positions (2, 4, 6)
    for (let i = 1; i < 7; i += 2) {
        sum += parseInt(digits.charAt(i), 10);
    }

    // Sum odd positions (1, 3, 5, 7) - double and sum digits
    for (let i = 0; i < 7; i += 2) {
        const doubled = parseInt(digits.charAt(i), 10) * 2;
        sum += Math.floor(doubled / 10) + (doubled % 10);
    }

    // Calculate control digit
    const unitDigit = sum % 10;
    const controlDigit = (10 - unitDigit) % 10;
    const controlLetter = String.fromCharCode(65 + controlDigit); // A=0, B=1, ..., J=9

    const control = cifUpper.charAt(8);
    // Control can be either the digit or the corresponding letter
    return control === String(controlDigit) || control === controlLetter;
};

// Validates Spanish document ID (DNI, NIE, or Passport)
export const validateDocumentId = (
    documentId: string,
): { valid: boolean; type: 'NIF_' | 'NIE_' | 'PASA' | 'CIF_' | '' } => {
    if (!documentId || documentId.trim() === '') {
        return { valid: false, type: '' };
    }

    if (validateDNI(documentId)) {
        return { valid: true, type: 'NIF_' };
    }

    if (validateNIE(documentId)) {
        return { valid: true, type: 'NIE_' };
    }

    if (validateCIF(documentId)) {
        return { valid: false, type: 'CIF_' };
    }

    if (validatePassport(documentId)) {
        return { valid: true, type: 'PASA' };
    }

    return { valid: false, type: '' };
};

// Validates Spanish phone number (mobile or landline)
// Accepts formats: 612345678, +34612345678, 34612345678
export const validatePhone = (phone: string): boolean => {
    if (!phone || phone.trim() === '') {
        return false;
    }

    // Remove spaces and common separators
    const cleanPhone = phone.replace(/[\s\-()]/g, '');

    const phoneRegex = /^(\+34|34)?[6-9][0-9]{8}$/;

    return phoneRegex.test(cleanPhone);
};

export const validateTextLength = (name: string, minLength: number): boolean => {
    if (!name || name.trim() === '') {
        return false;
    }

    return name.trim().length >= minLength;
};
